/**
 * Phase 1.7 — top header.
 *
 * Sits above the content (and right rail, when present). Houses:
 *   - sidebar toggle on mobile (the only place the sidebar is collapsible)
 *   - page title slot — set per page via PageTitleProvider
 *   - actions slot — right-aligned, set per page
 *   - theme toggle + user menu always at the far right
 *
 * The page title and actions are passed via context rather than props on
 * AppShell so that any descendant can override them mid-render
 * (e.g., a tab change inside a page swaps the action bar).
 */

import { Menu } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { useAuthStore, logout } from '@/lib/auth';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeaderContextShape {
  title: ReactNode;
  setTitle: (next: ReactNode) => void;
  actions: ReactNode;
  setActions: (next: ReactNode) => void;
  toggleSidebar: () => void;
  /**
   * Force the sidebar drawer closed. Sidebar consumes this on route
   * change and on link tap so the mobile drawer auto-dismisses without
   * needing a second tap on the backdrop.
   */
  closeSidebar: () => void;
  sidebarOpen: boolean;
}

const HeaderContext = createContext<HeaderContextShape | null>(null);

export function HeaderProvider({ children }: { children: ReactNode }): JSX.Element {
  const [title, setTitle] = useState<ReactNode>('');
  const [actions, setActions] = useState<ReactNode>(null);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const value = useMemo<HeaderContextShape>(
    () => ({ title, setTitle, actions, setActions, toggleSidebar, closeSidebar, sidebarOpen }),
    [title, actions, sidebarOpen, toggleSidebar, closeSidebar],
  );
  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
}

export function useHeader(): HeaderContextShape {
  const ctx = useContext(HeaderContext);
  if (!ctx) throw new Error('useHeader must be used inside <HeaderProvider>');
  return ctx;
}

/**
 * Page-level helper. Sets the header title + actions on mount, clears on
 * unmount. Pages should pass a stable `title` (string is fine) and either
 * memoize their actions or accept that they re-render the header on every
 * action-prop change.
 */
export function usePageHeader(title: ReactNode, actions?: ReactNode): void {
  const { setTitle, setActions } = useHeader();
  useEffect(() => {
    setTitle(title);
    setActions(actions ?? null);
    return () => {
      setTitle('');
      setActions(null);
    };
  }, [title, actions, setTitle, setActions]);
}

export function Header(): JSX.Element {
  const { title, actions, toggleSidebar, sidebarOpen } = useHeader();
  const userName = useAuthStore((s) => s.user?.name ?? '');

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-header-h items-center gap-3 border-b border-border bg-surface px-4',
        'dark:bg-surface-dark dark:border-border',
      )}
    >
      <button
        type="button"
        onClick={toggleSidebar}
        className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
        aria-label="Toggle navigation"
        aria-expanded={sidebarOpen}
        aria-controls="primary-sidebar"
      >
        <Menu className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        {typeof title === 'string' ? (
          <h1 className="truncate font-display text-xl text-charcoal dark:text-charcoal-dark">{title}</h1>
        ) : (
          title
        )}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        <ThemeToggle className="text-muted-foreground hover:text-charcoal dark:hover:text-charcoal-dark" />
        <span className="hidden sm:inline-block max-w-[10rem] truncate text-sm text-muted-foreground">
          {userName}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => void logout()}
          aria-label="Sign out"
          className="text-muted-foreground hover:text-charcoal dark:hover:text-charcoal-dark"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
